import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import { FetchApi, FetchApiNoParam } from "../../../utils/api";
import { Alert } from "antd";

// Add this constant at the top
const CART_STORAGE_KEY = "voucher_cart";

// Keep your existing interfaces (they're correct)
interface Voucher {
  // Add proper voucher properties here
  [key: string]: any;
}

interface PriceGroup {
  price_id: string;
  title: string;
  quantity: number;
  amount: string;
}

interface BookingParams {
  store_id: number;
  voucher_id: number;
  name: string;
  phone: string;
  email: string;
  referral: string;
  promotion_code: string;
  // datereserve: string;
  // timereserve: string;
  // number_adult: number;
  // number_child: number;
  price_group: PriceGroup[];
}

interface CurrentBooking {
  selectedProduct?: Voucher;
  bookingParams?: BookingParams;
}

interface VoucherVariant {
  price_id: string;
  title: string;
  amount: string;
  quantity: number;
}

interface NewVoucherCartItem {
  voucher_name: string;
  voucher_id: string;
  store_name: string;
  store_id: string;
  variant: VoucherVariant;
}

interface VoucherProduct {
  voucher_name: string;
  voucher_id: string;
  variants: VoucherVariant[];
}

interface Store {
  tempId: string;
  store_name: string;
  store_id: string;
  products: VoucherProduct[];
}

type VoucherCart = Store[];

interface VoucherBooking {
  voucher_title: string;
  status: string;
  usedate: string | null;
  valid_date: string;
  voucher_code: string;
  create_date: string;
  remark: string;
  orderid: string;
  merge_id: string | null;
  payment_type: string | null;
  payment_ref: string | null;
  store_id: string | null;
  store_name: string;
  amount: string | null;
  price_title?: string;
}

// ✅ FIXED: Correct interface
interface VoucherContextType {
  generateTempId: () => string;
  voucherCarts: VoucherCart;
  vouchers: Voucher[];
  voucherBookings: VoucherBooking[]; // Updated type
  voucherDetails: Voucher;
  loading: boolean;
  error: string | null;
  fetchVouchers: (parameters: {
    store_id?: number;
    search?: string;
  }) => Promise<Voucher[]>;
  fetchVoucherDetails: (voucher_id?: number) => Promise<Voucher>;
  createVoucherBooking: (params: BookingParams) => Promise<any>;
  currentBooking: CurrentBooking;
  onCurrentBooking: (booking: CurrentBooking) => void;
  addToCart: (item: NewVoucherCartItem) => void;
  removeFromCart: (
    storeTempId: string,
    voucherId?: string,
    priceId?: string
  ) => void;
  clearVariantFromProduct: (
    storeTempId: string,
    voucherId: string,
    priceId: string
  ) => void;
  fetchTempVoucherBooking: (tempBookingId: any) => void;
  clearCart: () => void;
  fetchVoucherBookings: (parameters: {
    UUID: string;
  }) => Promise<VoucherBooking[]>;
  groupedVoucherBookings: GroupedVoucherBooking[];
  getVoucherQRCode: (voucherCode: string) => Promise<string>; // For QR code generation
  fetchUserVoucherDetail: (voucherCode: string) => Promise<any>; // Add this line
}

// Add these interfaces to your existing interfaces in VoucherContext.tsx
interface GroupedVoucherBooking {
  orderid: string;
  merge_id: string | null;
  voucher_title: string;
  store_name: string;
  create_date: string;
  voucher_count: number;
  total_amount: string;
  payment_status: string;
  payment_type: string | null;
  payment_ref: string | null;
  voucher_types: Array<{
    type: string;
    count: number;
    amount: string;
  }>;
  individual_vouchers: VoucherBooking[];
  overall_status: string;
}

// Add this function to your VoucherContext (before the provider)
const groupVoucherBookings = (
  bookings: VoucherBooking[]
): GroupedVoucherBooking[] => {
  const groupedMap = new Map();

  bookings.forEach((booking: any) => {
    const key = booking.orderid;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        orderid: booking.orderid,
        merge_id: booking.merge_id,
        voucher_title: booking.voucher_title,
        store_name: booking.store_name,
        create_date: booking.create_date,
        voucher_count: 0,
        total_amount: "0.00",
        payment_status: booking.payment_status || "paid",
        payment_type: booking.payment_type,
        payment_ref: booking.payment_ref,
        voucher_types: [],
        individual_vouchers: [],
        overall_status: "active",
      });
    }

    const group = groupedMap.get(key);
    group.individual_vouchers.push(booking);
    group.voucher_count += 1;
    group.total_amount = (
      parseFloat(group.total_amount) + parseFloat(booking.amount || "0")
    ).toFixed(2);

    // Track voucher types
    const existingType = group.voucher_types.find(
      (type: any) => type.type === booking.price_title
    );
    if (existingType) {
      existingType.count += 1;
    } else {
      group.voucher_types.push({
        type: booking.price_title || "standard",
        count: 1,
        amount: booking.amount,
      });
    }

    // Update overall status (if any voucher is used, mark as used)
    if (booking.status === "1" || booking.usedate) {
      group.overall_status = "used";
    }
  });

  return Array.from(groupedMap.values());
};

const VoucherContext = createContext<VoucherContextType | undefined>(undefined);

export const VoucherProvider = ({ children }: { children: ReactNode }) => {
  const [groupedVoucherBookings, setGroupedVoucherBookings] = useState<
    GroupedVoucherBooking[]
  >([]);
  // ✅ FIXED: Correct state type with localStorage initialization
  const [voucherCarts, setVoucherCarts] = useState<VoucherCart>(() => {
    // Load cart from localStorage on initial render
    if (typeof window !== "undefined") {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        try {
          return JSON.parse(savedCart);
        } catch (error) {
          console.error("Error parsing saved cart:", error);
          return [];
        }
      }
    }
    return [];
  });

  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [voucherBookings, setVoucherBookings] = useState<any>([]);
  const [voucherDetails, setVoucherDetails] = useState<Voucher>([]);
  const [currentBooking, setCurrentBooking] = useState<CurrentBooking>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [bookingError, setBookingError] = useState<any>(null);

  // Save to localStorage whenever voucherCarts changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(voucherCarts));
    }
  }, [voucherCarts]);

  const generateTempId = () => {
    return Math.random().toString(36).substring(2, 10);
  };

  // Add this function inside your VoucherProvider
  const fetchTempVoucherBooking = (
    tempBookingId: string
  ): Store | undefined => {
    // Find the booking in the current cart by tempId
    const booking = voucherCarts.find(
      (store) => store.tempId === tempBookingId
    );

    if (!booking) {
      console.warn(`Booking with tempId ${tempBookingId} not found in cart`);
      return undefined;
    }

    const _currentBooking: CurrentBooking = {
      selectedProduct: booking,
      bookingParams: {
        voucher_id: Number(booking.products[0].voucher_id),
        store_id: Number(booking.store_id),
        name: "",
        email: "",
        phone: "",
        price_group: booking.products[0].variants,
        referral: "",
        promotion_code: "",
      },
    };

    console.log("check _currentBooking ===> ", _currentBooking);

    setCurrentBooking(_currentBooking);
  };

  // ✅ FIXED: Better typing
  const onCurrentBooking = (booking: CurrentBooking) =>
    setCurrentBooking(booking);

  const createVoucherBooking = useCallback(
    async (parameters: BookingParams): Promise<any> => {
      setBookingLoading(true);
      try {
        const url = FetchApiNoParam("create_voucher.php");
        console.log("check url createVoucherBooking ===> ", url);

        // Create FormData object
        const formData = new FormData();

        // Append basic fields
        formData.append("voucher_id", parameters.voucher_id.toString());
        formData.append("store_id", parameters.store_id.toString());
        // formData.append("voucher_id", parameters.voucher_id.toString());
        // formData.append("store_id", parameters.store_id.toString());
        formData.append("name", parameters.name);
        formData.append("email", parameters.email);
        formData.append("phone", parameters.phone);
        formData.append("referral", parameters.referral);
        formData.append("promotion_code", parameters.promotion_code);

        // Append price_group array
        if (parameters.price_group && parameters.price_group.length > 0) {
          parameters.price_group.forEach((price, index) => {
            formData.append(`price_group[${index}][price_id]`, price.price_id);
            formData.append(`price_group[${index}][title]`, price.title);
            formData.append(`price_group[${index}][amount]`, price.amount);
            formData.append(
              `price_group[${index}][quantity]`,
              price.quantity.toString()
            );
          });
        }

        // Log FormData contents for debugging
        console.log("FormData contents:");
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        const response = await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("check response createVoucherBooking ===> ", response);

        if (response.data.status === 1) return response.data;
        throw response.data;
      } catch (err) {
        setBookingError(err);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const fetchVouchers = async (parameters: {
    store_id?: number;
    search?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("voucher_search.php", parameters);
      const response = await axios.get(url);
      if (response.data) {
        setVouchers(response.data);
        return response.data;
      } else {
        setError("Invalid response");
        return Promise.reject(response.data);
      }
    } catch (err: any) {
      setError(err?.message || "Unknown error");
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVoucherDetails = async (voucher_id?: number) => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("voucher_detail.php", { voucher_id: voucher_id });
      console.log("check voucherDetails url ===> ", url);
      const response = await axios.get(url);
      if (response.data) {
        setVoucherDetails(response.data);
        return response.data;
      } else {
        setError("Invalid response");
        return Promise.reject(response.data);
      }
    } catch (err: any) {
      setError(err?.message || "Unknown error");
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  // Update fetchVoucherBookings to also group the data:
  const fetchVoucherBookings = async (parameters: {
    UUID: string;
  }): Promise<VoucherBooking[]> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("list_user_purchase_voucher.php", parameters);
      console.log("URL ======= fetchVoucherBooking", url);
      const response = await axios.get(url);

      if (response.data) {
        console.log(
          "Valid Response ======= fetchVoucherBooking",
          response.data
        );

        // Ensure we always set an array, even if response is empty
        const bookings = Array.isArray(response.data) ? response.data : [];
        setVoucherBookings(bookings);

        // Group the bookings
        const grouped = groupVoucherBookings(bookings);
        setGroupedVoucherBookings(grouped);

        return bookings;
      } else {
        console.log(
          "Invalid Response ======= fetchVoucherBooking",
          response.data
        );
        const errorMsg = "Invalid response from server";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.log("Catch Error ======= fetchVoucherBooking", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch bookings";
      setError(errorMsg);

      // Set empty arrays on error to prevent crashes
      setVoucherBookings([]);
      setGroupedVoucherBookings([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVoucherDetail = async (voucherCode: string): Promise<any> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("user_voucher_detail.php", {
        qrcode: voucherCode,
      });
      console.log(
        "Fetching voucher details for code:",
        voucherCode,
        "URL:",
        url
      );

      const response = await axios.get(url);

      if (response.data) {
        console.log("Voucher details response:", response.data);
        return response.data;
      } else {
        setError("Invalid response from server");
        return Promise.reject("Invalid response from server");
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to verify voucher";
      setError(errorMsg);
      return Promise.reject(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getVoucherQRCode = async (voucherCode: string): Promise<string> => {
    // For now, return a placeholder or generate QR code URL
    // You can integrate with a QR code generation service
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${voucherCode}`;
  };

  // ✅ Each voucher added becomes a separate booking (store)
  const addToCart = (newItem: NewVoucherCartItem) => {
    const updatedCart = [...voucherCarts];

    // Check if this product already exists in any booking
    let productExists = false;

    for (const store of updatedCart) {
      const productIndex = store.products.findIndex(
        (product) => product.voucher_id === newItem.voucher_id
      );

      if (productIndex !== -1) {
        // Product exists in this store, find the variant
        const product = store.products[productIndex];
        const variantIndex = product.variants.findIndex(
          (variant) => variant.price_id === newItem.variant.price_id
        );

        if (variantIndex === -1) {
          // Variant doesn't exist, add new variant to existing product
          product.variants.push(newItem.variant);
        } else {
          // Variant exists, update quantity
          product.variants[variantIndex].quantity += newItem.variant.quantity;
        }

        productExists = true;
        break;
      }
    }

    if (!productExists) {
      // Product doesn't exist, create new booking with this product
      const newStore: Store = {
        tempId: generateTempId(), // Unique ID for this booking
        store_name: newItem.store_name,
        store_id: newItem.store_id,
        products: [
          {
            voucher_name: newItem.voucher_name,
            voucher_id: newItem.voucher_id,
            variants: [newItem.variant],
          },
        ],
      };
      updatedCart.push(newStore);
    }

    setVoucherCarts(updatedCart);
    // localStorage is automatically updated via useEffect
  };

  // ✅ Remove entire booking (store) or specific product/variant
  const removeFromCart = (
    storeTempId: string,
    voucherId?: string,
    priceId?: string
  ) => {
    let updatedCart: VoucherCart;

    if (!voucherId) {
      // Remove entire booking (store)
      updatedCart = voucherCarts.filter(
        (store) => store.tempId !== storeTempId
      );
    } else {
      // Remove specific product or variant within the booking
      updatedCart = voucherCarts
        .map((store) => {
          if (store.tempId !== storeTempId) return store;

          if (priceId) {
            // Remove specific variant
            return {
              ...store,
              products: store.products
                .map((product) => {
                  if (product.voucher_id !== voucherId) return product;
                  return {
                    ...product,
                    variants: product.variants.filter(
                      (variant) => variant.price_id !== priceId
                    ),
                  };
                })
                .filter((product) => product.variants.length > 0), // Remove empty products
            };
          } else {
            // Remove entire product
            return {
              ...store,
              products: store.products.filter(
                (product) => product.voucher_id !== voucherId
              ),
            };
          }
        })
        .filter((store) => store.products.length > 0); // Remove empty stores
    }

    setVoucherCarts(updatedCart);
    // localStorage is automatically updated via useEffect
  };

  // ✅ Clear specific variant from product
  const clearVariantFromProduct = (
    storeTempId: string,
    voucherId: string,
    priceId: string
  ) => {
    removeFromCart(storeTempId, voucherId, priceId);
  };

  // ✅ FIXED: Implement clearCart - this will clear both state and localStorage
  const clearCart = () => {
    setVoucherCarts([]);
    // localStorage will be automatically cleared via useEffect when state becomes empty array
  };

  // Optional: Add a function to clear cart on logout if needed
  const clearCartOnLogout = () => {
    setVoucherCarts([]);
    if (typeof window !== "undefined") {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  };

  return (
    <VoucherContext.Provider
      value={{
        generateTempId,
        voucherCarts,
        vouchers,
        voucherBookings,
        voucherDetails,
        fetchVoucherDetails,
        currentBooking,
        loading,
        error,
        fetchVouchers,
        onCurrentBooking,
        createVoucherBooking,
        addToCart,
        removeFromCart,
        clearVariantFromProduct,
        fetchTempVoucherBooking,
        clearCart,
        fetchVoucherBookings,
        groupedVoucherBookings,
        getVoucherQRCode,
        fetchUserVoucherDetail,
      }}
    >
      {children}
    </VoucherContext.Provider>
  );
};

export const useVoucher = () => {
  const context = useContext(VoucherContext);
  if (!context) {
    throw new Error("useVoucher must be used within VoucherProvider");
  }
  return context;
};
